import * as Sentry from '@sentry/nextjs'
import axios from 'axios'
import type { User } from 'firebase/auth'

import { getReferralServiceClient, type RedeemPromoCodeRequestProductType } from '@/clients/referral-service'

import { baseAxiosConfig } from './api/rest/config'
import { getUserAuthHeaders } from './getUserAuthHeaders'

const client = getReferralServiceClient(
  axios.create({ ...baseAxiosConfig, baseURL: process.env.NEXT_PUBLIC_REFERRAL_SERVICE_BASE }),
)

export const getCampaignStatus = async (identifier: string) => {
  try {
    const res = await client.getStatusForCampaign(identifier)
    return res.status === 200 ? res.data : undefined
  } catch (e) {
    Sentry.captureException(e, { extra: { context: `Error fetching campaign with identifier ${identifier}` } })
    return { status: 'ERROR' }
  }
}

export const getEligibleCampaignForUser = async (user: User | null) => {
  if (!user) {
    return null
  }
  try {
    const res = await client.getEligibleCampaignForUser({ headers: await getUserAuthHeaders(user) })
    return res.data
  } catch (e) {
    Sentry.captureException(e, { extra: { context: 'Error fetching eligible campaign for user' } })
    return null
  }
}

/**
 * Result of referee signup. Discriminated by success so coupon fields are only present when success is true.
 */
export type RefereeSignUpResult = { success: true; coupon_code: string; coupon_id: string } | { success: false }

/**
 * Referee signup: creates promo code for referee, stores it, and sends email.
 * Uses POST /public/referrals/referee-signup (refereeSignedUp).
 * Request body: referrer_username, campaign_identifier, referee_email, marketing_consent.
 */
export const refereeSignedUp = async (
  referrer_username: string,
  campaign_identifier: string,
  referee_email: string,
  marketing_consent: boolean,
  referrer_link_sharing_source: string,
): Promise<RefereeSignUpResult> => {
  try {
    const res = await client.refereeSignedUp({
      referrer_username,
      campaign_identifier,
      referee_email,
      marketing_consent,
      referrer_link_sharing_source,
    })
    if (res.status === 200 && res.data) {
      return {
        success: true,
        coupon_code: res.data.promo_code?.code ?? '',
        coupon_id: res.data.promo_code?.id ?? '',
      }
    }
    return { success: false }
  } catch (e) {
    Sentry.captureException(e, {
      extra: {
        context: `Error creating referral for campaign ${campaign_identifier}, referrer ${referrer_username}`,
      },
    })
    return { success: false }
  }
}

export const getReferrerRewards = async (user: User | null) => {
  if (!user) {
    return null
  }

  try {
    const response = await client.getReferrerRewards({ headers: await getUserAuthHeaders(user) })

    if (response.status === 200) {
      return response.data
    }

    return null
  } catch (e) {
    Sentry.captureException(e, { extra: { context: 'Error fetching referrer rewards' } })
    return null
  }
}

export const getReferrerSubscriptions = async (user: User | null, referrerUsername: string) => {
  if (!user || !referrerUsername) {
    return null
  }

  try {
    const response = await client.getReferrerSubscriptions({ headers: await getUserAuthHeaders(user) })

    if (response.status === 200) {
      return response.data
    }

    return null
  } catch (e) {
    Sentry.captureException(e, { extra: { context: 'Error fetching referrer subscriptions' } })
    return null
  }
}

export const redeemReferrerPromoCode = async (
  user: User | null,
  subscriptionId: string,
  promoCodeId: string,
  productType: string,
) => {
  if (!user) {
    return { success: false, error: 'Not authenticated' }
  }

  try {
    await client.redeemReferrerPromoCode(
      subscriptionId,
      { promo_code_id: promoCodeId, product_type: productType as RedeemPromoCodeRequestProductType },
      { headers: await getUserAuthHeaders(user) },
    )
    return { success: true }
  } catch (e) {
    Sentry.captureException(e, { extra: { context: 'Error redeeming promo code' } })
    return { success: false, error: 'Unable to apply voucher.' }
  }
}
